import {Box} from '@mui/material';
import {
  GridCallbackDetails,
  GridRowHeightParams,
  GridRowHeightReturnValue,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import {t} from 'i18next';
import ArrDataGridPro, {
  ArrDataGridProProps,
  SelectionControlProps,
} from '../../../../common/arr-data-grid-pro/ArrDataGridPro';
import React, {useEffect, useRef, useState} from 'react';
import {ColumnConfig} from '../../config/reportTypes';
import {DataGridRowSelectionModel} from '../../../../../shared/interfaces';
import './ReportDataGrid.scss';
const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

type ReportsDataGridProps = {
  rows: any;
  loading?: boolean;
  columns: ColumnConfig[];
  getRowId?: (row: any) => string | number;
  aditionalGridProps?: ReportGridProps;
};

export interface ReportGridProps extends SelectionControlProps {
  primaryKey?: string;
  totalRecords?: number;
  disableVirtualization?: boolean;
  className?: string;
  enableCheckbox?: boolean;
  currentPage?: number;
  setCurrentPage?: (arg0: number) => void;
  itemsPerPage?: number;
  setItemsPerPage?: (arg0: number) => void;
  onPageChange?: (pagenumber: number, pageSize?: number) => void;
  pageSizeOptions?: number[];
  entityType?: string;
  sortModel?: GridSortModel;
  paginationMode?: ArrDataGridProProps['paginationMode'];
  sortingMode?: ArrDataGridProProps['sortingMode'];
  hideFooter?: ArrDataGridProProps['hideFooter'];
  getRowClassName?: ArrDataGridProProps['getRowClassName'];
  onSortModelChange?: (
    model: GridSortModel,
    details: GridCallbackDetails
  ) => void;
  getRowHeight?:
    | ((params: GridRowHeightParams) => GridRowHeightReturnValue)
    | undefined;
  getEstimatedRowHeight?:
    | ((params: GridRowHeightParams) => number | null)
    | undefined;
  onSelectionStateChange?: (
    rowSelectionModal: DataGridRowSelectionModel
  ) => void;
  alwaysShowPagination?: boolean;
  disableDataGridPagination?: boolean;
  showPerPageOptions?: boolean;
  disableColumnResize?: boolean;
}
const ReportsDataGrid: React.FC<ReportsDataGridProps> = ({
  rows,
  loading,
  columns,
  getRowId,
  aditionalGridProps,
}) => {
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {field: 'userEmail', sort: 'asc'},
  ]);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const dataGridContainerRef = useRef<HTMLDivElement>(null);
  const [dataGridContainerWidth, setDataGridContainerWidth] =
    useState<number>(0);
  useEffect(() => {
    if (!dataGridContainerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      setDataGridContainerWidth((prev) => {
        const next = entry.contentRect.width;
        return Math.abs(prev - next) < 1 ? prev : next;
      });
    });

    observer.observe(dataGridContainerRef.current);
    return () => observer.disconnect();
  }, []);

  const columnsWithTabIndex = columns.map((col) => ({
    ...col,
    renderHeader: (params) => (
      <span
        tabIndex={0}
        role="columnheader"
        title={params.colDef.headerName ?? params.field}
        className="cell-content"
        data-testid={`report-data-grid-header-${params.colDef.headerName ?? params.field}`}
      >
        {params.colDef.headerName ?? params.field}
      </span>
    ),
  }));

  return (
    <Box
      ref={dataGridContainerRef}
      className="report-data-grid"
      data-grid-width={dataGridContainerWidth}
    >
      <ArrDataGridPro
        className={aditionalGridProps?.className ?? ''}
        data-testid="doc-explorer-data-grid"
        primaryKey={aditionalGridProps?.primaryKey ?? 'userId'}
        columns={columnsWithTabIndex}
        disableColumnResize={aditionalGridProps?.disableColumnResize ?? true}
        currentPage={aditionalGridProps?.currentPage ?? pageNumber}
        paginationMode={aditionalGridProps?.paginationMode ?? 'server'}
        sortingMode={aditionalGridProps?.sortingMode ?? 'server'}
        paginationModel={{
          pageSize: aditionalGridProps?.itemsPerPage ?? pageSize,
          page: aditionalGridProps?.disableDataGridPagination
            ? 0
            : (aditionalGridProps?.currentPage ?? pageNumber) - 1,
        }}
        hideFooter={aditionalGridProps?.hideFooter}
        rowCount={aditionalGridProps?.totalRecords ?? rows?.totalRecords ?? 0}
        showPerPageOptions={aditionalGridProps?.showPerPageOptions ?? true}
        itemsPerPage={aditionalGridProps?.itemsPerPage ?? pageSize}
        setItemsPerPage={aditionalGridProps?.setItemsPerPage ?? setPageSize}
        onPageChange={aditionalGridProps?.onPageChange ?? setPageNumber}
        pageSizeOptions={
          aditionalGridProps?.pageSizeOptions ?? PAGE_SIZE_OPTIONS
        }
        rows={rows}
        getRowClassName={aditionalGridProps?.getRowClassName}
        pagination
        sortModel={aditionalGridProps?.sortModel ?? sortModel}
        onSortModelChange={
          aditionalGridProps?.onSortModelChange ?? setSortModel
        }
        loading={loading}
        disableVirtualization={
          aditionalGridProps?.disableVirtualization ?? true
        }
        getRowId={getRowId}
        noRowsOverlayCustomText={t(
          'docExplorer.docDataGrid.noRowsOverlayCustomText'
        )}
        enableCheckbox={aditionalGridProps?.enableCheckbox ?? false}
        entityType={aditionalGridProps?.entityType ?? ''}
        getRowHeight={aditionalGridProps?.getRowHeight}
        getEstimatedRowHeight={aditionalGridProps?.getEstimatedRowHeight}
        onSelectionStateChange={aditionalGridProps?.onSelectionStateChange}
        selectedIds={aditionalGridProps?.selectedIds}
        unselectedIds={aditionalGridProps?.unselectedIds}
        isAllSelected={aditionalGridProps?.isAllSelected}
        alwaysShowPagination={aditionalGridProps?.alwaysShowPagination}
        autoHeight={false}
      />
    </Box>
  );
};

export default ReportsDataGrid;
